import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Users, Clock3, Search, RefreshCcw, User as UserIcon, MapPin, Phone, Smartphone, Mail, Camera, ImageOff } from "lucide-react";

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("tr-TR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function timeAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso).getTime();
  if (Number.isNaN(d)) return iso;
  const diff = Math.max(0, (Date.now() - d) / 1000);
  if (diff < 60) return `${Math.floor(diff)} sn önce`;
  if (diff < 3600) return `${Math.floor(diff/60)} dk önce`;
  if (diff < 86400) return `${Math.floor(diff/3600)} saat önce`;
  if (diff < 30*86400) return `${Math.floor(diff/86400)} gün önce`;
  return `${Math.floor(diff/(30*86400))} ay önce`;
}

const TABS = [
  { key: "summary", label: "Ortak Özeti" },
  { key: "events",  label: "Ziyaret Kayıtları" },
];

const ACTION_META = {
  "profile_update:adres":  { label: "Adres",      icon: MapPin,     tone: "bg-amber-50 text-amber-700 ring-amber-200" },
  "profile_update:tel1":   { label: "Telefon 1",  icon: Phone,      tone: "bg-sky-50 text-sky-700 ring-sky-200" },
  "profile_update:tel2":   { label: "Telefon 2",  icon: Phone,      tone: "bg-sky-50 text-sky-700 ring-sky-200" },
  "profile_update:gsm1":   { label: "GSM 1",      icon: Smartphone, tone: "bg-indigo-50 text-indigo-700 ring-indigo-200" },
  "profile_update:gsm2":   { label: "GSM 2",      icon: Smartphone, tone: "bg-indigo-50 text-indigo-700 ring-indigo-200" },
  "profile_update:email":  { label: "E-posta",    icon: Mail,       tone: "bg-teal-50 text-teal-700 ring-teal-200" },
  "photo_added":           { label: "Fotoğraf eklendi",     icon: Camera,   tone: "bg-emerald-50 text-emerald-700 ring-emerald-200" },
  "photo_updated":         { label: "Fotoğraf güncellendi", icon: Camera,   tone: "bg-emerald-50 text-emerald-700 ring-emerald-200" },
  "photo_removed":         { label: "Fotoğraf silindi",     icon: ImageOff, tone: "bg-rose-50 text-rose-700 ring-rose-200" },
};

function ActionBadges({ actions }) {
  if (!actions || actions.length === 0) {
    return <span className="text-[11px] text-slate-400">—</span>;
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {actions.map(a => {
        const meta = ACTION_META[a] || { label: a, icon: UserIcon, tone: "bg-slate-50 text-slate-700 ring-slate-200" };
        const Icon = meta.icon;
        return (
          <span key={a}
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${meta.tone}`}
            title={meta.label}>
            <Icon size={11}/> {meta.label}
          </span>
        );
      })}
    </div>
  );
}

function Avatar({ src, name }) {
  const initials = (name || "").split(" ").filter(Boolean).slice(0,2).map(x => x[0]).join("").toLocaleUpperCase("tr-TR") || "?";
  if (src) return <img src={src} alt={name} className="h-8 w-8 rounded-full object-cover ring-1 ring-slate-200"/>;
  return <div className="h-8 w-8 rounded-full bg-slate-100 border border-dashed border-slate-300 flex items-center justify-center text-[10px] font-bold text-slate-400">{initials}</div>;
}

export default function MemberVisits() {
  const [tab, setTab] = useState("summary");
  const [summary, setSummary] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/member-visits/summary`, { withCredentials: true }).then(r => r.data || []),
      axios.get(`${API}/member-visits`, { params: { limit: 500 }, withCredentials: true }).then(r => r.data || []),
    ]).then(([s, e]) => { setSummary(s); setEvents(e); })
      .catch(() => toast.error("Ziyaret kayıtları yüklenemedi"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const query = q.trim().toLocaleLowerCase("tr-TR");
  const visibleSummary = useMemo(() => summary.filter(m => {
    if (!query) return true;
    return (
      (m.ad_soyad || "").toLocaleLowerCase("tr-TR").includes(query) ||
      (m.tc_kimlik || "").includes(query)
    );
  }), [summary, query]);

  const visibleEvents = useMemo(() => events.filter(v => {
    if (!query) return true;
    return (
      (v.ad_soyad || "").toLocaleLowerCase("tr-TR").includes(query) ||
      (v.tc_kimlik || "").includes(query) ||
      (v.ip || "").toLocaleLowerCase("tr-TR").includes(query)
    );
  }), [events, query]);

  return (
    <div className="space-y-6" data-testid="member-visits-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500 mb-2">Ortak Girişleri</div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Ortak Ziyaretleri</h1>
          <p className="text-slate-600 mt-2 text-sm">Panele giren ortakların son ziyaret zamanları ve tarihçesi.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-[11px] text-slate-500 font-mono-nums">
            <span className="font-semibold text-slate-700">{summary.length}</span> giriş yapan ortak · <span className="font-semibold text-slate-700">{events.length}</span> ziyaret
          </div>
          <button data-testid="visits-refresh" onClick={load}
            className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
            <RefreshCcw size={14}/> Yenile
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            data-testid={`visits-tab-${t.key}`}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${tab === t.key ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}>
            {t.label}
          </button>
        ))}
        <div className="relative ml-auto">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
          <input value={q} onChange={e => setQ(e.target.value)} data-testid="visits-search"
                 placeholder="Ad, TC veya IP ara..."
                 className="pl-9 pr-3 py-2 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-900 text-sm bg-white w-64"/>
        </div>
      </div>

      <div className="crisp-card overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-sm text-slate-500">Yükleniyor...</div>
        ) : (tab === "summary" ? visibleSummary.length === 0 : visibleEvents.length === 0) ? (
          <div className="p-10 text-center text-slate-500">
            <Users size={26} className="mx-auto mb-2 text-slate-400"/>
            <div className="text-sm">Henüz kayıt yok.</div>
          </div>
        ) : tab === "summary" ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
                <tr>
                  <th className="pl-4 pr-2 py-3 w-12"></th>
                  <th className="text-left px-4 py-3">Ortak</th>
                  <th className="text-left px-4 py-3">TC Kimlik</th>
                  <th className="text-left px-4 py-3">Son Ziyaret</th>
                  <th className="text-right px-4 py-3">Toplam Giriş</th>
                  <th className="text-left px-4 py-3">Yaptığı İşlemler</th>
                  <th className="text-left px-4 py-3">Son IP</th>
                </tr>
              </thead>
              <tbody>
                {visibleSummary.map(m => (
                  <tr key={m.id} data-testid={`visits-summary-row-${m.id}`} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="pl-4 pr-2 py-2"><Avatar src={m.photo_thumb} name={m.ad_soyad}/></td>
                    <td className="px-4 py-3">
                      <Link to={`/ortak/${m.id}`} className="font-semibold text-slate-900 hover:text-slate-700">{m.ad_soyad}</Link>
                    </td>
                    <td className="px-4 py-3 font-mono-nums text-slate-700">{m.tc_kimlik || "—"}</td>
                    <td className="px-4 py-3 font-mono-nums text-slate-700 whitespace-nowrap">
                      <div>{fmt(m.last_login_at)}</div>
                      <div className="text-[11px] text-slate-500">{timeAgo(m.last_login_at)}</div>
                    </td>
                    <td className="px-4 py-3 text-right font-mono-nums font-bold text-slate-900">{m.login_count || 1}</td>
                    <td className="px-4 py-3"><ActionBadges actions={m.actions}/></td>
                    <td className="px-4 py-3 font-mono-nums text-[12px] text-slate-500">{m.last_login_ip || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
                <tr>
                  <th className="text-left px-4 py-3">Zaman</th>
                  <th className="text-left px-4 py-3">Ortak</th>
                  <th className="text-left px-4 py-3">TC Kimlik</th>
                  <th className="text-left px-4 py-3">Yaptığı İşlemler</th>
                  <th className="text-left px-4 py-3">IP</th>
                  <th className="text-left px-4 py-3">Tarayıcı</th>
                </tr>
              </thead>
              <tbody>
                {visibleEvents.map(v => (
                  <tr key={v.id} data-testid={`visits-event-row-${v.id}`} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono-nums text-slate-700 whitespace-nowrap">
                      <div>{fmt(v.at)}</div>
                      <div className="text-[11px] text-slate-500">{timeAgo(v.at)}</div>
                    </td>
                    <td className="px-4 py-3">
                      <Link to={`/ortak/${v.member_id}`} className="font-semibold text-slate-900 hover:text-slate-700 inline-flex items-center gap-2">
                        <UserIcon size={12} className="text-slate-400"/> {v.ad_soyad || "—"}
                      </Link>
                    </td>
                    <td className="px-4 py-3 font-mono-nums text-slate-700">{v.tc_kimlik || "—"}</td>
                    <td className="px-4 py-3"><ActionBadges actions={v.actions}/></td>
                    <td className="px-4 py-3 font-mono-nums text-[12px] text-slate-500">{v.ip || "—"}</td>
                    <td className="px-4 py-3 text-[11px] text-slate-500 max-w-[300px] truncate" title={v.user_agent}>{v.user_agent || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
